import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

/**
 * Snapshot de atribucion UTM. Todos los campos opcionales — al menos
 * `source` deberia venir para que la fila tenga valor (sin source la
 * orden cae en "Organico" en el reporte).
 */
export interface UtmAttribution {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
}

const SESSION_STORAGE_KEY_PREFIX = 'pln_utm_';
// Limita longitud porque la columna en DB esta truncada a 200 chars
// y porque URLs largas tienden a ser basura (tracking de redes que
// pegan parametros largos sin valor para nosotros).
const MAX_FIELD_LENGTH = 200;

/**
 * Captura los UTM parameters del query string del menu publico y los
 * persiste en sessionStorage por slug. Sobrevive a navegaciones internas
 * (`/<slug>` -> `/<slug>/page` -> checkout) pero NO entre dominios ni
 * pestañas distintas — la atribucion es por sesion de navegacion.
 *
 * El primer UTM gana: si el cliente abre un link con UTM, navega por el
 * sitio, recibe otro link con otro UTM y vuelve, mantenemos el original
 * (atribucion first-click). Esto evita que la "ultima visita" se quede
 * con el credito de un click que vino del organico.
 *
 * El servicio se inicializa una sola vez en bootstrap del app (via
 * `provideAppInitializer` o llamando init() desde AppComponent) y luego
 * escucha cambios de ruta para capturar UTMs en cada navegacion que
 * traiga query params nuevos.
 */
@Injectable({ providedIn: 'root' })
export class UtmTrackingService {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private initialized = false;

  /**
   * Hook llamado desde AppComponent / APP_INITIALIZER. Capturamos UTMs
   * del URL inicial (NavigationEnd no se dispara para el primer landing
   * si Angular ya hidrato) y nos suscribimos a NavigationEnd para
   * capturas posteriores.
   */
  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    // Captura inicial: el snapshot ya tiene los query params del primer load.
    this.captureFromUrl(this.parseQueryString(window.location.search));

    // Capturas siguientes: cada vez que cambia la ruta (deep link interno
    // con nuevos UTMs, ej. el cliente comparte un link de un producto
    // especifico con su propio UTM).
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(event => {
        const queryStart = event.urlAfterRedirects.indexOf('?');
        if (queryStart === -1) return;
        const queryString = event.urlAfterRedirects.slice(queryStart);
        this.captureFromUrl(this.parseQueryString(queryString));
      });
  }

  /**
   * Devuelve la atribucion UTM activa para el slug actual. Si no hay
   * UTM capturado (cliente vino organico, escribiendo la URL a mano),
   * devuelve {} — el handler backend ignora los campos vacios.
   */
  getAttribution(slug: string | null | undefined): UtmAttribution {
    if (!slug) return {};
    try {
      const raw = sessionStorage.getItem(this.storageKey(slug));
      if (!raw) return {};
      return JSON.parse(raw) as UtmAttribution;
    } catch {
      return {};
    }
  }

  /**
   * Limpia la atribucion de un slug. Util tras crear orden si quieres
   * que la siguiente compra del mismo cliente no reuse el UTM viejo.
   * Hoy no lo llamamos automaticamente — el cliente que repite suele
   * volver por el mismo medio igual.
   */
  clear(slug: string): void {
    try { sessionStorage.removeItem(this.storageKey(slug)); } catch { /* ignored */ }
  }

  private captureFromUrl(params: URLSearchParams): void {
    const source = this.read(params, 'utm_source');
    if (!source) return; // sin source no hay atribucion que guardar

    const slug = this.extractSlugFromPath(window.location.pathname);
    if (!slug) return;

    // First-click wins: si ya hay UTM guardado para este slug, no lo pisamos.
    const existing = this.getAttribution(slug);
    if (existing.utmSource) return;

    const attribution: UtmAttribution = {
      utmSource: source,
      utmMedium: this.read(params, 'utm_medium'),
      utmCampaign: this.read(params, 'utm_campaign'),
      utmContent: this.read(params, 'utm_content'),
      utmTerm: this.read(params, 'utm_term')
    };

    try {
      sessionStorage.setItem(this.storageKey(slug), JSON.stringify(attribution));
    } catch { /* sessionStorage puede fallar en modo privado de Safari */ }
  }

  /**
   * Extrae el slug del path `/<slug>` o `/<slug>/...`. Devuelve null para
   * rutas que no pertenecen a un tenant (`/`, `/info`, `/login`, etc.).
   *
   * Lista negra: rutas conocidas del shell. Si en el futuro se agregan
   * rutas raiz nuevas hay que sumarlas aqui.
   */
  private extractSlugFromPath(path: string): string | null {
    const segments = path.split('/').filter(Boolean);
    if (segments.length === 0) return null;
    const first = segments[0].toLowerCase();
    const reserved = new Set([
      'info', 'catalog', 'login', 'register', 'admin', 'superadmin',
      'owner', 'encuesta', 'rastrear-pedido', 'order-success'
    ]);
    if (reserved.has(first)) return null;
    return segments[0];
  }

  private read(params: URLSearchParams, key: string): string | undefined {
    const raw = params.get(key);
    if (!raw) return undefined;
    const trimmed = raw.trim();
    if (!trimmed) return undefined;
    return trimmed.length > MAX_FIELD_LENGTH ? trimmed.slice(0, MAX_FIELD_LENGTH) : trimmed;
  }

  private parseQueryString(queryString: string): URLSearchParams {
    return new URLSearchParams(queryString.startsWith('?') ? queryString.slice(1) : queryString);
  }

  private storageKey(slug: string): string {
    return `${SESSION_STORAGE_KEY_PREFIX}${slug.toLowerCase()}`;
  }
}
